/**
 * ArchiveSetupPanel - Setup Wizard for Archive Connection
 *
 * Platform-specific setup UI:
 * - Electron: PostgreSQL + Ollama configuration
 * - Web: Sign-in prompt
 *
 * @module @humanizer/studio/components/archive/ArchiveSetupPanel
 */

import React, { useState, useCallback } from 'react';
import { useArchive, type ElectronArchiveConfig, type ArchivePlatform } from '../../contexts/ArchiveContext';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface ArchiveSetupPanelProps {
  onLoginRequest?: () => void;
  className?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// ELECTRON SETUP
// ═══════════════════════════════════════════════════════════════════════════

function ElectronSetup() {
  const { services, configure, refresh } = useArchive();
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Form state with defaults
  const [postgresConfig, setPostgresConfig] = useState({
    host: 'localhost',
    port: 5432,
    database: 'humanizer_archive',
    user: 'postgres',
    password: '',
  });

  const [ollamaConfig, setOllamaConfig] = useState({
    url: 'http://localhost:11434',
  });

  // Test connection and save
  const handleSetup = useCallback(async () => {
    setIsConfiguring(true);
    setError(null);

    const config: ElectronArchiveConfig = {
      postgres: postgresConfig,
      ollama: ollamaConfig,
    };

    const result = await configure(config);

    if (result.success) {
      // Refresh to update status
      await refresh();
    } else {
      setError(result.error || 'Configuration failed');
    }

    setIsConfiguring(false);
  }, [postgresConfig, ollamaConfig, configure, refresh]);

  // Service status indicators
  const postgresStatus = services?.postgres;
  const ollamaStatus = services?.ollama;

  return (
    <div className="archive-setup">
      <div className="archive-setup__header">
        <h3 className="archive-setup__title">Local Archive Setup</h3>
        <p className="archive-setup__description">
          Configure your local database to store and search your content archive.
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="archive-setup__error">
          <span className="archive-setup__error-icon">⚠️</span>
          <span className="archive-setup__error-text">{error}</span>
        </div>
      )}

      {/* PostgreSQL Section */}
      <div className="archive-setup__section">
        <div className="archive-setup__section-header">
          <span className="archive-setup__section-icon">🐘</span>
          <span className="archive-setup__section-title">PostgreSQL Database</span>
          <span className={`archive-setup__status ${postgresStatus?.available ? 'archive-setup__status--connected' : 'archive-setup__status--disconnected'}`}>
            {postgresStatus?.available ? '✓ Connected' : postgresStatus?.configured ? '○ Configured' : '○ Not configured'}
          </span>
        </div>

        <div className="archive-setup__form">
          <div className="archive-setup__form-row">
            <label className="archive-setup__label">
              Host
              <input
                type="text"
                className="archive-setup__input"
                value={postgresConfig.host}
                onChange={(e) => setPostgresConfig((c) => ({ ...c, host: e.target.value }))}
                placeholder="localhost"
              />
            </label>
            <label className="archive-setup__label archive-setup__label--small">
              Port
              <input
                type="number"
                className="archive-setup__input"
                value={postgresConfig.port}
                onChange={(e) => setPostgresConfig((c) => ({ ...c, port: parseInt(e.target.value) || 5432 }))}
                placeholder="5432"
              />
            </label>
          </div>

          <div className="archive-setup__form-row">
            <label className="archive-setup__label">
              Database
              <input
                type="text"
                className="archive-setup__input"
                value={postgresConfig.database}
                onChange={(e) => setPostgresConfig((c) => ({ ...c, database: e.target.value }))}
                placeholder="humanizer_archive"
              />
            </label>
          </div>

          {showAdvanced && (
            <>
              <div className="archive-setup__form-row">
                <label className="archive-setup__label">
                  User
                  <input
                    type="text"
                    className="archive-setup__input"
                    value={postgresConfig.user}
                    onChange={(e) => setPostgresConfig((c) => ({ ...c, user: e.target.value }))}
                    placeholder="postgres"
                  />
                </label>
              </div>

              <div className="archive-setup__form-row">
                <label className="archive-setup__label">
                  Password
                  <input
                    type="password"
                    className="archive-setup__input"
                    value={postgresConfig.password}
                    onChange={(e) => setPostgresConfig((c) => ({ ...c, password: e.target.value }))}
                    placeholder="(optional)"
                  />
                </label>
              </div>
            </>
          )}

          <button
            className="archive-setup__toggle-advanced"
            onClick={() => setShowAdvanced(!showAdvanced)}
            type="button"
            aria-expanded={showAdvanced}
          >
            {showAdvanced ? 'Hide' : 'Show'} advanced options
          </button>
        </div>

        {!postgresStatus?.available && (
          <div className="archive-setup__help">
            <p>
              <strong>Need PostgreSQL?</strong> Install{' '}
              <a href="https://postgresapp.com" target="_blank" rel="noopener noreferrer">
                Postgres.app
              </a>{' '}
              (macOS) or{' '}
              <a href="https://www.postgresql.org/download/" target="_blank" rel="noopener noreferrer">
                PostgreSQL
              </a>
              .
            </p>
            <p>Make sure to enable the <code>pgvector</code> extension for semantic search.</p>
          </div>
        )}
      </div>

      {/* Ollama Section */}
      <div className="archive-setup__section">
        <div className="archive-setup__section-header">
          <span className="archive-setup__section-icon">🦙</span>
          <span className="archive-setup__section-title">Ollama (AI Models)</span>
          <span className={`archive-setup__status ${ollamaStatus?.available ? 'archive-setup__status--connected' : ollamaStatus?.configured ? 'archive-setup__status--disconnected' : 'archive-setup__status--optional'}`}>
            {ollamaStatus?.available ? '✓ Connected' : ollamaStatus?.configured ? '○ Not running' : '○ Optional'}
          </span>
        </div>

        <div className="archive-setup__form">
          <div className="archive-setup__form-row">
            <label className="archive-setup__label">
              Ollama URL
              <input
                type="text"
                className="archive-setup__input"
                value={ollamaConfig.url}
                onChange={(e) => setOllamaConfig((c) => ({ ...c, url: e.target.value }))}
                placeholder="http://localhost:11434"
              />
            </label>
          </div>
        </div>

        {ollamaStatus?.available && ollamaStatus.models && ollamaStatus.models.length > 0 && (
          <div className="archive-setup__models">
            <span className="archive-setup__models-label">Available models:</span>
            <span className="archive-setup__models-list">
              {ollamaStatus.models.slice(0, 5).join(', ')}
              {ollamaStatus.models.length > 5 && ` +${ollamaStatus.models.length - 5} more`}
            </span>
          </div>
        )}

        {!ollamaStatus?.available && (
          <div className="archive-setup__help">
            <p>
              <strong>Optional:</strong> Install{' '}
              <a href="https://ollama.com" target="_blank" rel="noopener noreferrer">
                Ollama
              </a>{' '}
              for local AI features (humanization, embeddings).
            </p>
          </div>
        )}
      </div>

      {/* Action */}
      <div className="archive-setup__actions">
        <button
          className="archive-setup__button archive-setup__button--primary"
          onClick={handleSetup}
          disabled={isConfiguring}
        >
          {isConfiguring ? 'Connecting...' : 'Connect to Database'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// WEB SETUP (Sign In)
// ═══════════════════════════════════════════════════════════════════════════

interface WebSetupProps {
  onLoginRequest?: () => void;
}

function WebSetup({ onLoginRequest }: WebSetupProps) {
  return (
    <div className="archive-setup archive-setup--web">
      <div className="archive-setup__header">
        <h3 className="archive-setup__title">Cloud Archive</h3>
        <p className="archive-setup__description">
          Sign in to access your archive stored securely in the cloud.
        </p>
      </div>

      <div className="archive-setup__cloud-info">
        <div className="archive-setup__cloud-features">
          <div className="archive-setup__cloud-feature">
            <span className="archive-setup__cloud-feature-icon">☁️</span>
            <span>Secure cloud storage</span>
          </div>
          <div className="archive-setup__cloud-feature">
            <span className="archive-setup__cloud-feature-icon">🔍</span>
            <span>Semantic search</span>
          </div>
          <div className="archive-setup__cloud-feature">
            <span className="archive-setup__cloud-feature-icon">🔄</span>
            <span>Sync across devices</span>
          </div>
        </div>
      </div>

      <div className="archive-setup__actions">
        <button
          className="archive-setup__button archive-setup__button--primary"
          onClick={onLoginRequest}
        >
          Sign in with Google
        </button>
      </div>

      <p className="archive-setup__privacy">
        Your data is stored securely on humanizer.com servers.
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function ArchiveSetupPanel({ onLoginRequest, className = '' }: ArchiveSetupPanelProps) {
  const { platform } = useArchive();

  return (
    <div className={`archive-setup-panel ${className}`}>
      {platform === 'electron' ? (
        <ElectronSetup />
      ) : (
        <WebSetup onLoginRequest={onLoginRequest} />
      )}
    </div>
  );
}

export default ArchiveSetupPanel;
